import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase/config';

// Garmin connects direct (not via Terra) — see functions/src/garminAuth.ts.
// Credentials (client ID/secret) never reach the client; the callables below
// proxy to Garmin's OAuth2 PKCE endpoints server-side, same trust model as
// terraService.ts.

export interface GarminAuthResponse {
  url: string;
}

// Terra-connected Garmin devices also use resource "GARMIN"; only devices
// tagged by the direct integration belong to the callables below.
export const isDirectGarminDevice = (device: { resource: string; integration?: string }): boolean =>
  device.resource === 'GARMIN' && device.integration === 'direct';

export const generateGarminAuthUrl = async (): Promise<GarminAuthResponse> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const callable = httpsCallable<Record<string, never>, GarminAuthResponse>(
    functions,
    'generateGarminAuthUrl',
  );

  try {
    const result = await callable({});
    return result.data;
  } catch (error) {
    console.error('Garmin API Error:', error);
    throw error;
  }
};

/**
 * Deauthenticate the caller's own Garmin device. The Cloud Function calls
 * Garmin's registration-delete endpoint, then marks the device expired in
 * Firestore regardless of Garmin's response — same resilience pattern as
 * deauthenticateTerraDevice.
 */
export const deauthenticateGarminDevice = async (deviceId: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const callable = httpsCallable<{ deviceId: string }, { success: boolean }>(
    functions,
    'deauthenticateOwnGarminDevice',
  );

  try {
    await callable({ deviceId });
  } catch (error) {
    console.error('Error deauthenticating Garmin device:', error);
    throw new Error(
      'Failed to disconnect device. Please check your connection and try again.',
    );
  }
};
